
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {CurrencyGroupDeleteResponse} from './CurrencyGroupDeleteResponse';

/**
 * CurrencyGroupDeleteRequest struct
 */
class CurrencyGroupDeleteRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} currencyGroupId
   * @param {string=} currencyGroupName
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), currencyGroupId = null, currencyGroupName = null) {
    super()
    super.copy(parent)
    this.CurrencyGroupId = currencyGroupId
    this.CurrencyGroupName = currencyGroupName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!CurrencyGroupDeleteRequest}
   * @param {!CurrencyGroupDeleteRequest} other Other struct
   * @returns {!CurrencyGroupDeleteRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.CurrencyGroupId != null) {
      this.CurrencyGroupId = new UUID(other.CurrencyGroupId)
    } else {
      this.CurrencyGroupId = null
    }
    if (other.CurrencyGroupName != null) {
      this.CurrencyGroupName = other.CurrencyGroupName
    } else {
      this.CurrencyGroupName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!CurrencyGroupDeleteRequest}
   * @returns {!CurrencyGroupDeleteRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new CurrencyGroupDeleteRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new CurrencyGroupDeleteRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!CurrencyGroupDeleteRequest}
   * @param {!CurrencyGroupDeleteRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof CurrencyGroupDeleteRequest)) {
      throw new TypeError('Instance of CurrencyGroupDeleteRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!CurrencyGroupDeleteRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      CurrencyGroupId: ((this.CurrencyGroupId != null) ? this.CurrencyGroupId.toString() : null),
      CurrencyGroupName: ((this.CurrencyGroupName != null) ? this.CurrencyGroupName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return CurrencyGroupDeleteRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!CurrencyGroupDeleteRequest} other Object value
   * @returns {!CurrencyGroupDeleteRequest} Created struct
   */
  static fromObject (other) {
    return new CurrencyGroupDeleteRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!CurrencyGroupDeleteRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return CurrencyGroupDeleteRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!CurrencyGroupDeleteRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6082
  }
}

export { CurrencyGroupDeleteRequest };

/**
 * Fast Binary Encoding CurrencyGroupDeleteRequest field model
 */
class FieldModelCurrencyGroupDeleteRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.CurrencyGroupId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.CurrencyGroupName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.CurrencyGroupId.fbeOffset + this.CurrencyGroupId.fbeSize), buffer, this.CurrencyGroupId.fbeOffset + this.CurrencyGroupId.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.CurrencyGroupId.fbeSize + this.CurrencyGroupName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.CurrencyGroupId.fbeExtra + this.CurrencyGroupName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelCurrencyGroupDeleteRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6082
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.CurrencyGroupId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CurrencyGroupId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CurrencyGroupId.fbeSize

    if ((fbeCurrentSize + this.CurrencyGroupName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CurrencyGroupName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CurrencyGroupName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @param {!CurrencyGroupDeleteRequest} fbeValue Default value, defaults is new CurrencyGroupDeleteRequest()
   * @returns {!CurrencyGroupDeleteRequest} CurrencyGroupDeleteRequest value
   */
  get (fbeValue = new CurrencyGroupDeleteRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @param {!CurrencyGroupDeleteRequest} fbeValue CurrencyGroupDeleteRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.CurrencyGroupId.fbeSize) <= fbeStructSize) {
      fbeValue.CurrencyGroupId = this.CurrencyGroupId.get()
    } else {
      fbeValue.CurrencyGroupId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CurrencyGroupId.fbeSize

    if ((fbeCurrentSize + this.CurrencyGroupName.fbeSize) <= fbeStructSize) {
      fbeValue.CurrencyGroupName = this.CurrencyGroupName.get()
    } else {
      fbeValue.CurrencyGroupName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CurrencyGroupName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @param {!CurrencyGroupDeleteRequest} fbeValue CurrencyGroupDeleteRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelCurrencyGroupDeleteRequest}
   * @param {CurrencyGroupDeleteRequest} fbeValue CurrencyGroupDeleteRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.CurrencyGroupId.set(fbeValue.CurrencyGroupId)
    this.CurrencyGroupName.set(fbeValue.CurrencyGroupName)
  }
}

export { FieldModelCurrencyGroupDeleteRequest };

/**
 * Fast Binary Encoding CurrencyGroupDeleteRequest model
 */
class CurrencyGroupDeleteRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelCurrencyGroupDeleteRequest(this.buffer, 4)
  }

  /**
   * Get the CurrencyGroupDeleteRequest model
   * @this {!CurrencyGroupDeleteRequestModel}
   * @returns {!FieldModelCurrencyGroupDeleteRequest} model CurrencyGroupDeleteRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!CurrencyGroupDeleteRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!CurrencyGroupDeleteRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return CurrencyGroupDeleteRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!CurrencyGroupDeleteRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelCurrencyGroupDeleteRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!CurrencyGroupDeleteRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!CurrencyGroupDeleteRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!CurrencyGroupDeleteRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!CurrencyGroupDeleteRequestModel}
   * @param {!CurrencyGroupDeleteRequest} value CurrencyGroupDeleteRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!CurrencyGroupDeleteRequestModel}
   * @param {!CurrencyGroupDeleteRequest} value CurrencyGroupDeleteRequest value, defaults is new CurrencyGroupDeleteRequest()
   * @return {!object} Deserialized CurrencyGroupDeleteRequest value and its size
   */
  deserialize (value = new CurrencyGroupDeleteRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new CurrencyGroupDeleteRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new CurrencyGroupDeleteRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!CurrencyGroupDeleteRequestModel}
   * @param {!number} prev Previous CurrencyGroupDeleteRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { CurrencyGroupDeleteRequestModel };
CurrencyGroupDeleteRequest.__has_response = true;
CurrencyGroupDeleteRequest.__response_class = CurrencyGroupDeleteResponse;
